// A chart in a coach's reply (body.charts, the backend's coach/head/chart.py),
// as chart.js wants it. Pure: CoachChart.jsx draws what these return. A chart
// that doesn't match the contract is skipped, never drawn half right.
//
// A series may carry `type` ('bar' | 'line'), `axis` ('left' | 'right'), `unit`
// and `format`: bars and a line together, on two scales. Without them a chart is
// one kind on one axis. A right axis only exists beside a left one.

const TYPES = ['bar', 'line', 'area'];
const SERIES_TYPES = ['bar', 'line'];
const FORMATS = ['number', 'pace', 'duration'];
// Tell apart on the app's light and dark surfaces; the accent comes first (CoachChart).
export const SERIES_COLOURS = ['#3B82F6', '#D97706', '#C026D3'];
const LABELLED_POINTS = 12;                 // past this, values on the bars collide in the chat panel
const DOTTED_POINTS = 24;

const isNumber = (v) => typeof v === 'number' && Number.isFinite(v);

export function isDrawable(c) {
  if (!c || !TYPES.includes(c.type) || !Array.isArray(c.labels) || !c.labels.length) return false;
  if (!Array.isArray(c.series) || !c.series.length) return false;
  return c.series.every((s) => Array.isArray(s?.values) && s.values.length === c.labels.length
    && s.values.every((v) => v === null || isNumber(v))) && c.series.some((s) => s.values.some(isNumber));
}

export const drawableCharts = (charts) => (Array.isArray(charts) ? charts.filter(isDrawable) : []);

const clock = (big, small) => `${big}:${String(small).padStart(2, '0')}`;

/** A value as the axis shows it: 4:50, 1:30, 24.2. */
export function tick(value, format) {
  const v = Math.round(Number(value));
  if (format === 'pace') return clock(Math.floor(v / 60), v % 60);
  if (format === 'duration') return clock(Math.floor(v / 3600), Math.floor(v / 60) % 60);
  return String(Math.round(Number(value) * 10) / 10);
}

/** A value with its unit: 4:50/km, 1:30 h, 24.2 km. */
export function valueText(value, chart) {
  if (value == null) return 'no data';
  const unit = (chart.unit || '').trim();
  const joined = /^[/%]/.test(unit) ? unit : unit && ` ${unit}`;
  return `${tick(value, chart.format)}${joined}`;
}

/** How a series is drawn: its own type, else the chart's. */
export const seriesKind = (chart, s) => (SERIES_TYPES.includes(s?.type) ? s.type : chart.type);
/** A series' numbers: its own format and unit, else the chart's. */
export const seriesUnits = (chart, s) => ({
  format: FORMATS.includes(s?.format) ? s.format : chart.format,
  unit: typeof s?.unit === 'string' ? s.unit : chart.unit,
});
/** Bars and a line (or area) together. */
export const isMixed = (chart) => new Set(chart.series.map((s) => seriesKind(chart, s) === 'bar')).size > 1;
export const hasRightAxis = (chart) => chart.series.some((s) => s.axis === 'right') && chart.series.some((s) => s.axis !== 'right');
const onAxis = (chart, side) => chart.series.filter((s) => (side === 'right') === (hasRightAxis(chart) && s.axis === 'right'));
/** The format and unit an axis shows: those of its first series. */
export const axisUnits = (chart, side = 'left') => seriesUnits(chart, onAxis(chart, side)[0]);
const hasBars = (chart, list) => list.some((s) => seriesKind(chart, s) === 'bar');

/** The screen reader's chart: what it is, and each series' points or range. */
export function chartSummary(chart) {
  const lines = chart.series.map((s) => {
    const units = seriesUnits(chart, s);
    const points = s.values.map((v, i) => [chart.labels[i], v]).filter(([, v]) => v != null);
    const body = points.length <= LABELLED_POINTS
      ? points.map(([l, v]) => `${l} ${valueText(v, units)}`).join(', ')
      : `${points.length} points from ${valueText(Math.min(...points.map(([, v]) => v)), units)} to ${valueText(Math.max(...points.map(([, v]) => v)), units)}`;
    return s.name ? `${s.name}: ${body}` : body;
  });
  return `${chart.title}. ${lines.join('. ')}`;
}

/** A single series of a few bars carries its values, so no one reads across to the axis. */
export const showsValues = (chart) => chart.type === 'bar' && chart.series.length === 1 && !chart.stacked
  && chart.labels.length <= LABELLED_POINTS;

/**
 * Where a pace bar's axis starts. Splits sit seconds apart, so from zero every
 * bar looks the same: they start below the fastest, on a round half minute, and
 * the axis says so. Anything else starts at zero.
 */
export function barFloor(chart, side = 'left') {
  const list = onAxis(chart, side);
  if (!hasBars(chart, list) || axisUnits(chart, side).format !== 'pace' || chart.stacked) return 0;
  const fastest = Math.min(...list.flatMap((s) => s.values).filter(isNumber));
  return Math.max(0, Math.floor((fastest - 15) / 30) * 30);
}

/** What chart.js needs on top for a canvas of two kinds, or two axes. */
export const rootType = (chart) => (hasBars(chart, chart.series) ? 'bar' : 'line');

function mixedDataset(chart, s, i) {
  const bar = seriesKind(chart, s) === 'bar';
  return {
    type: bar ? 'bar' : 'line',
    yAxisID: hasRightAxis(chart) && s.axis === 'right' ? 'y1' : 'y',
    order: bar ? 2 : 1,                       // the line over the bars
    ...(bar ? {} : { stack: `line-${i}` }),    // stacking is for bars; a line stays where it is
  };
}

export function chartData(chart, accent) {
  const colours = [accent, ...SERIES_COLOURS];
  const dotted = chart.labels.length <= DOTTED_POINTS;
  const extended = isMixed(chart) || hasRightAxis(chart);
  return {
    labels: chart.labels,
    datasets: chart.series.map((s, i) => {
      const colour = colours[i % colours.length];
      const kind = seriesKind(chart, s);
      return {
        label: s.name || chart.title,
        data: s.values,
        borderColor: colour,
        backgroundColor: kind === 'area' ? `${colour}33` : colour,
        ...(kind === 'bar' ? { borderRadius: 3, maxBarThickness: 36 } : {
          borderWidth: 2, tension: 0.25, fill: kind === 'area', spanGaps: false,
          pointRadius: dotted ? 2.5 : 0, pointHoverRadius: 4, pointBackgroundColor: colour,
        }),
        ...(extended ? mixedDataset(chart, s, i) : {}),
      };
    }),
  };
}

/** A right axis, pace reads as it does on a line: faster is up (`reverse` in the backend's chart.py). */
function rightAxis(chart, { text }) {
  const list = onAxis(chart, 'right');
  const { format, unit } = axisUnits(chart, 'right');
  const bars = hasBars(chart, list);
  return {
    position: 'right',
    reverse: format === 'pace' && !bars,
    beginAtZero: bars,
    ...(bars ? { min: barFloor(chart, 'right') } : {}),
    grid: { drawOnChartArea: false },
    border: { display: false },
    title: { display: Boolean(unit), text: unit, color: text, font: { size: 10 } },
    ticks: { color: text, font: { size: 10 }, maxTicksLimit: 5, callback: (v) => tick(v, format) },
  };
}

export function chartOptions(chart, { text, grid }) {
  const legend = chart.series.length > 1;
  const floor = barFloor(chart);
  const left = axisUnits(chart);
  const right = hasRightAxis(chart);
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    layout: { padding: { top: showsValues(chart) ? 16 : 4 } },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: legend, position: 'bottom', labels: { color: text, boxWidth: 10, boxHeight: 10, font: { size: 11 },
        sort: (a, b) => a.datasetIndex - b.datasetIndex } },        // in series order, whichever is drawn on top
      tooltip: { callbacks: { label: (ctx) => `${legend ? `${ctx.dataset.label}: ` : ''}${valueText(ctx.parsed.y, seriesUnits(chart, chart.series[ctx.datasetIndex]))}` } },
    },
    scales: {
      x: {
        stacked: chart.stacked,
        offset: rootType(chart) === 'bar',
        grid: { display: false },
        border: { color: grid },
        ticks: { color: text, font: { size: 10 }, maxRotation: 0, autoSkipPadding: 8 },
      },
      y: {
        stacked: chart.stacked,
        reverse: Boolean(chart.reverse),
        ...(hasBars(chart, onAxis(chart, 'left')) ? { min: floor } : isMixed(chart) ? { beginAtZero: false } : {}),
        grid: { color: grid },
        border: { display: false },
        ...(right ? { title: { display: Boolean(left.unit), text: left.unit, color: text, font: { size: 10 } } } : {}),
        ticks: { color: text, font: { size: 10 }, maxTicksLimit: 5, callback: (v) => tick(v, left.format) },
      },
      ...(right ? { y1: rightAxis(chart, { text }) } : {}),
    },
  };
}
